import {Color} from '@benchling/nucleus-ui';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import omitKey from 'util/omitKey';
import {Optional} from 'util/Types';

import ErrorMessage from '../ErrorMessage';
import Dropdown, {closeDropdown} from './Dropdown';

export enum SelectDropdownErrorStyle {
  INLINE = 'INLINE',
  TOOLTIP = 'TOOLTIP',
}

export interface CornerRadius {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
}

function createCornerRadiusCss(cornerRadius: CornerRadius): {buttonCorners: StyleDeclaration} {
  return StyleSheet.create({
    buttonCorners: {
      borderTopLeftRadius: cornerRadius.topLeft,
      borderTopRightRadius: cornerRadius.topRight,
      borderBottomLeftRadius: cornerRadius.bottomLeft,
      borderBottomRightRadius: cornerRadius.bottomRight,
    },
  });
}

export interface SelectDropdownProps {
  id?: string;
  buttonSize?: ButtonSize;
  closeOnInsideClick?: boolean;
  closeOnOutsideClick?: boolean;
  disabled?: boolean;
  displayName: React.ReactNode;
  dropdownToggleOverride?: JSX.Element;
  // Whether to use flexGrow to take up remainder of button space outside of the caret dropdown.
  shouldGrowDisplayName?: boolean;
  // Whether to ellipsize the display name text. If false, text will overflow to another row.
  shouldEllipsize?: boolean;
  error?: Optional<string | JSX.Element>;
  errorStyle?: SelectDropdownErrorStyle;
  errorTooltipPlacement?: FloatingPlacement;
  // Icon to render to the left of `displayName`
  icon?: BenchlingIconName;
  iconClassName?: string;
  // Can be controlled by the parent; otherwise, open state is changed from a click toggle.
  isOpen?: boolean;
  /**
   * If isOpen and at least one of closeOnInsideClick / closeOnOutsideClick are also passed, this must be
   * passed in order for the latter to work.
   */
  onToggle?: (isOpen: boolean) => void;
  menu: JSX.Element;
  tooltipToggleDisplayMode?: ToggleDisplayMode;
  // Applies to both the top level div of this component and the absolute dropdown container
  className?: Optional<string>;
  // Applies only to the dropdown button element
  buttonClassName?: Optional<string>;
  autoFocus?: boolean;
  alwaysDropDown?: boolean;
  // If no cornerRadius provided, dropdown will have all rounded corners. This prop allows custom corner rounding.
  cornerRadius?: CornerRadius;
  dataTestAttrs?: DataTestAttrs;
}

type SelectDropdownToggleProps = Pick<
  SelectDropdownProps,
  | 'autoFocus'
  | 'buttonClassName'
  | 'buttonSize'
  | 'disabled'
  | 'displayName'
  | 'error'
  | 'icon'
  | 'iconClassName'
  | 'isOpen'
  | 'onToggle'
  | 'shouldEllipsize'
  | 'shouldGrowDisplayName'
  | 'cornerRadius'
  /** onClick and onKeyDown are passed in by DropdownToggle, which ultimately wraps this component. */
> & {onClick?: () => void; onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void};

/**
 * Exported for testing.
 */
export function SelectDropdownToggle({
  autoFocus,
  buttonClassName,
  buttonSize,
  disabled,
  displayName,
  error,
  icon,
  iconClassName,
  onClick,
  onKeyDown,
  shouldEllipsize,
  shouldGrowDisplayName,
  cornerRadius,
}: SelectDropdownToggleProps): JSX.Element {
  return (
    <ButtonWrapperForTooltip disabled={disabled} onClick={onClick}>
      <button
        className={classNames(buttonClassName, 'btn-af-default', 'selectDropdown-button', {
          [css(selectDropdownStyles.errorBorder)]: error && !disabled,
          'is-disabled': disabled,
          [css(selectDropdownStyles.disabled)]: disabled,
          [`button--${buttonSize}`]: Boolean(buttonSize),
          [css(cornerRadius && createCornerRadiusCss(cornerRadius).buttonCorners)]: cornerRadius,
        })}
        type="button"
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        {...getTestProps({component: 'SelectDropdown', element: 'input'})}
      >
        <div className={css(selectDropdownStyles.buttonContent)}>
          {icon && (
            <div className={css(selectDropdownStyles.iconWrapper)}>
              <BenchlingIcon className={iconClassName} icon={icon} />
            </div>
          )}
          <span
            className={classNames(
              icon && css(selectDropdownStyles.iconFormat),
              'selectDropdown-text',
              css(selectDropdownStyles.selectDropdownText),
              shouldEllipsize && css(selectDropdownStyles.ellipsizedText),
              shouldGrowDisplayName && css(selectDropdownStyles.growDisplayName)
            )}
          >
            {displayName}
          </span>
          <BenchlingIcon
            className={classNames(
              css(selectDropdownStyles.buttonCaret),
              disabled && css(selectDropdownStyles.disabledCaret)
            )}
            icon="chevron-down"
          />
        </div>
      </button>
    </ButtonWrapperForTooltip>
  );
}

interface ButtonWrapperForTooltipProps {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

// jQuery UI does not guarantee any level of support for tooltips attached
// to disabled elements (https://api.jqueryui.com/tooltip/),
// so we use this wrapper to pass the disabled prop to DropdownToggle
// and avoid setting the disabled prop on the jQuery button directly.
/** @package */
export function ButtonWrapperForTooltip(props: ButtonWrapperForTooltipProps): JSX.Element {
  return <div onClick={props.onClick}>{props.children}</div>;
}

export function closeSelectDropdown(selectDropdown: SelectDropdown): void {
  if (selectDropdown.dropdown) {
    closeDropdown(selectDropdown.dropdown);
  }
}

/**
 * Select-style dropdown (our version of <select>...</select>) with a default selection.
 *
 * The parent is responsible for updating the display name to match the current selection.
 * See /_design/dropdowns and DropdownGuidelines for example usage.
 */
export default class SelectDropdown extends React.Component<SelectDropdownProps, {}> {
  static propTypes = {
    alwaysDropDown: PropTypes.bool,
    buttonSize: PropTypes.oneOf(Object.values(ButtonSize)),
    className: PropTypes.string,
    closeOnInsideClick: PropTypes.bool,
    closeOnOutsideClick: PropTypes.bool,
    disabled: PropTypes.bool.isRequired,
    displayName: PropTypes.node.isRequired,
    dropdownToggleOverride: PropTypes.element,
    shouldEllipsize: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    errorStyle: PropTypes.oneOf(Object.values(SelectDropdownErrorStyle)),
    errorTooltipPlacement: PropTypes.oneOf(Object.values(FloatingPlacement)),
    icon: PropTypes.string,
    iconClassName: PropTypes.string,
    isOpen: PropTypes.bool,
    menu: PropTypes.node.isRequired, // A <Menu /> component.
    autoFocus: PropTypes.bool,
    dataTestAttrs: PropTypes.object,
  };

  static defaultProps = {
    // Inherit most defaults from Dropdown, but omit closeOnInsideClick since we determine it dynamically
    ...omitKey(Dropdown.defaultProps, 'closeOnInsideClick'),
    disabled: false,
    errorStyle: SelectDropdownErrorStyle.INLINE,
    shouldEllipsize: false,
  };

  dropdown?: Dropdown;

  _getTooltipText(): Optional<string> {
    if (this.props.error != null && typeof this.props.error !== 'string') {
      return null;
    }

    if (this.props.errorStyle === SelectDropdownErrorStyle.TOOLTIP && this.props.error) {
      return this.props.error;
    }
    // If the dropdown is disabled and ellipsized, users may not be able to see what value is selected, so we
    // try to render a tooltip. Tooltips don't support arbitrary React nodes, which displayName does, so we
    // only show it if we detect it's a string.
    if (this.props.shouldEllipsize && typeof this.props.displayName === 'string' && this.props.disabled) {
      // Tooltips cannot be empty strings, must supply null instead
      return this.props.displayName !== '' ? this.props.displayName : null;
    }
    return null;
  }

  _renderDropdownToggle(): JSX.Element {
    if (this.props.dropdownToggleOverride != null) {
      return this.props.dropdownToggleOverride;
    } else {
      return (
        <SelectDropdownToggle
          autoFocus={this.props.autoFocus}
          buttonClassName={this.props.buttonClassName}
          buttonSize={this.props.buttonSize}
          disabled={this.props.disabled}
          displayName={this.props.displayName}
          error={this.props.error}
          icon={this.props.icon}
          iconClassName={this.props.iconClassName}
          shouldEllipsize={this.props.shouldEllipsize}
          shouldGrowDisplayName={this.props.shouldGrowDisplayName}
          cornerRadius={this.props.cornerRadius}
        />
      );
    }
  }

  override render(): JSX.Element {
    const tooltip = this._getTooltipText();
    const isMultiSelect =
      React.isValidElement(this.props.menu) && (this.props.menu as React.ReactElement).props.isMulti;
    const closeOnInsideClick = this.props.closeOnInsideClick ?? !isMultiSelect;

    return (
      <div
        className={classNames('selectDropdown', this.props.className)}
        {...getTestProps(this.props.dataTestAttrs ?? {component: 'SelectDropdown', element: 'root'})}
      >
        <Tooltip
          toggleDisplayMode={this.props.tooltipToggleDisplayMode}
          text={tooltip}
          placement={this.props.errorTooltipPlacement}
          disableContentSelection={true}
        >
          <div className={css(selectDropdownStyles.tooltip)}>
            <Dropdown
              {...omitKey(this.props, 'id')}
              closeOnInsideClick={closeOnInsideClick}
              ref={(node: Dropdown) => {
                this.dropdown = node;
              }}
            >
              {this._renderDropdownToggle()}
            </Dropdown>
          </div>
        </Tooltip>
        {this.props.error && this.props.errorStyle === SelectDropdownErrorStyle.INLINE && (
          <ErrorMessage
            dataTestAttrs={{component: 'SelectDropdown', element: 'errorMessage'}}
            error={this.props.error}
          />
        )}
      </div>
    );
  }
}

/** @package */
export const selectDropdownStyles = StyleSheet.create({
  buttonContent: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttonCaret: {
    color: Colors.GRAY_DARKER,
    flexShrink: 0,
    marginLeft: '5px',
  },
  disabled: {
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
  },
  disabledCaret: {
    color: Color.GRAY_600,
  },
  iconWrapper: {
    alignItems: 'center',
    color: Colors.GRAY,
    display: 'flex',
    justifyContent: 'center',
    width: '1em',
    marginRight: '10px',
  },
  iconFormat: {
    flex: 1,
    minWidth: 0 /* Prevent overflow */,
  },
  selectDropdownText: {
    overflowWrap: 'break-word',
    minWidth: 0,
    letterSpacing: 'initial',
  },
  tooltip: {
    width: '100%',
  },
  growDisplayName: {
    flexGrow: 1,
  },
  ellipsizedText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  errorBorder: {
    border: '1px solid',
    borderColor: Colors.RED_DARK,
  },
});
